import { ClipboardService, PageService } from '@aston/foundation/services';
import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Remarkable } from 'remarkable';

const md = new Remarkable({ html: true });

import { linkButton, systemMessage, temporaryText } from '../../functions/utils.ai';

export const FLAKY_RESPONSE_TOKEN = '⠀';

export interface AiMessage {
	text?: string
	html?: string
}

export interface AiEntityToken {
	type: string
	id: string
	name: string
}

export interface AiEntityAction {
	kind: 'open' | 'copy-id'
	label: string
	route?: string
}

export interface IAiInterceptor {
	handleClipboardCopy: (e: Event) => void
	hydrateResponse: (r: any) => any[]
	hydrateFullResponse: (fullText: string) => string | null
	handleClick: (e: any) => void
	handleEntityClick: (e: Event) => boolean
	closeAllEntityMenus: (root: Document | ShadowRoot | HTMLElement) => void
}

@Injectable({ providedIn: 'any' })
export class AIChatBoxMessagesInterceptor implements IAiInterceptor {
	pageTitle = toSignal(inject(PageService).pageTitle$)
	i18n = inject(TranslateService)
	cb = inject(ClipboardService)
	fatStore = inject(Store)
	router = inject(Router)

	isFlakyResponse = signal(false)
	flakyResponsesCount = signal(0)
	private lastFlakyResponse = signal(0)

	// Complete AI response with replaced tokens and actions buttons
	public hydrateResponse(response: any): AiMessage[] {
		if (!response?.text) return [response];
		const replaced = this.replaceWellKnownTokens(response.text)
		return [{ ...response, text: replaced }, this.generateTemporaryButtons(response.text)]
	}

	// Post-stream: replace entity tokens in the full text and return html if badges present
	public hydrateFullResponse(fullText: string): string | null {
		const replaced = this.replaceTextAndEntityTokens(fullText, t => md.render(this.transformFullResponseText(t)))
		return replaced.includes('ai-entity-token') ? replaced : null
	}

	protected transformFullResponseText(text: string): string {
		return text
	}

	handleClipboardCopy(event: Event) {
		let wrapper = event.target as HTMLElement;
		while (wrapper?.classList && !wrapper.classList.contains('ai-message-text') && wrapper !== document.body) {
			wrapper = wrapper.parentElement as HTMLElement;
		}
		if (wrapper?.classList?.contains('ai-message-text')) {
			const el = wrapper.querySelector('.stream-wrapper') as HTMLElement
			if (el) this.cb.writeText(el.textContent || el.innerText)
		}
	}

	// Handle clicks inside AI response texts
	handleClick = (event: any): boolean => {
		if (event.originalTarget instanceof HTMLAnchorElement) {
			const element = event.originalTarget as HTMLAnchorElement;
			const target = element.getAttribute('target');
			const route = element.getAttribute('href');
			if (route?.startsWith('/') && target === '_blank') {
				event.preventDefault();
				this.router.navigateByUrl(route);
				return false;
			}
		}
		return true;
	}

	handleEntityClick = (event: Event): boolean => {
		const target = this.eventElement(event.target);
		if (!target) return true;
		const action = this.closest(target, 'ai-entity-action');
		const token = this.closest(target, 'ai-entity-token');
		if (!token) return true;

		event.preventDefault();
		event.stopPropagation();

		if (action?.dataset?.entityAction === 'open') {
			const route = action.dataset.entityRoute || token.dataset.entityRoute;
			if (route) this.router.navigateByUrl(route);
			this.closeEntityMenu(token);
			return false;
		}

		if (action?.dataset?.entityAction === 'copy-id') {
			const id = token.dataset.entityId;
			if (id) this.cb.writeText(id);
			this.closeEntityMenu(token);
			return false;
		}

		if (action?.dataset?.entityAction === 'write-text') {
			let dp: any = token;
			while (dp.parentNode && !dp.host?.submitUserMessage) {
				dp = dp.parentNode;
			}
			if (dp.host?.submitUserMessage) {
				dp.host.submitUserMessage(action.innerText)
			}

			this.closeEntityMenu(token);
			return false;
		}

		this.toggleEntityMenu(token);
		return false;
	}

	generateTemporaryButtons(text: string) {
		const buttons: string[] = []
		// buttons.push(suggestionButton('Comment le DSO est calculé ?'))
		return systemMessage({ html: temporaryText(buttons.join('')) })
	}

	replaceWellKnownTokens(text: string) {
		this.trackFlakyResponse(text)
		return this.replaceEntityTokens(text)
	}

	protected trackFlakyResponse(text: string) {
		this.isFlakyResponse.set(false)
		if (text.includes(FLAKY_RESPONSE_TOKEN)) {
			this.flakyResponsesCount.update(c => c + 1)
		}
		if (this.flakyResponsesCount() !== this.lastFlakyResponse()) {
			this.lastFlakyResponse.set(this.flakyResponsesCount())
			this.isFlakyResponse.set(true)
		}
	}

	buttonsFromFlakyResponse() {
		const contactUs = this.i18n.instant('Support.Contact')
		const byEmail = this.i18n.instant('Support.EmailText')
		const byPhone = this.i18n.instant('Support.PhoneText')
		const byCalls = this.i18n.instant('Support.MeetUrlText')
		const buttons = []

		if (this.flakyResponsesCount() % 20 === 0) {
			buttons.push(linkButton(`${contactUs} ${byCalls}`, this.i18n.instant('Support.MeetUrl')))
		} else if (this.flakyResponsesCount() % 15 === 0) {
			buttons.push(linkButton(`${contactUs} ${byPhone}`, `tel:${this.i18n.instant('Support.Phone')}`))
		} else if (this.flakyResponsesCount() % 10 === 0) {
			buttons.push(linkButton(`${contactUs} ${byEmail}`, `mailto:${this.i18n.instant('Support.Email')}`))
		}

		return buttons
	}

	protected replaceEntityTokens(text: string) {
		return this.replaceTextAndEntityTokens(text, value => value)
	}

	protected replaceTextAndEntityTokens(text: string, replaceText: (text: string) => string) {
		const entities: string[] = []
		// Plain word placeholder: survives Remarkable as inline text (unlike __x__ → emphasis, <!--x--> → breaks list parsing)
		const tokenized = text.replace(/\[entity:([^:\]]+):([^:\]]+):([^\]]+)]/g, (_, type: string, id: string, name: string) => {
			const placeholder = `AICHATENTITY${entities.length}AICHATENTITY`
			entities.push(this.entityBadge({ type, id, name }))
			return placeholder
		})

		return replaceText(tokenized).replace(/AICHATENTITY(\d+)AICHATENTITY/g, (placeholder, index: string) =>
			entities[Number(index)] ?? placeholder
		)
	}

	protected entityBadge(entity: AiEntityToken) {
		const route = this.getEntityRoute(entity)
		const rawType = this.escapeHtml(entity.type)
		const displayType = this.escapeHtml(this.getEntityType(entity.type))
		const id = this.escapeHtml(entity.id)
		const name = this.escapeHtml(entity.name)
		const routeAttribute = route ? ` data-entity-route="${this.escapeHtml(route)}"` : ''
		const openAction = route
			? `<button type="button" class="ai-entity-action" data-entity-action="open" data-entity-route="${this.escapeHtml(route)}" role="menuitem" style="display: block; width: 100%; border: 0; background: transparent; padding: 6px 12px; text-align: left; cursor: pointer; font: inherit; color: #333;">${this.i18n.instant('Actions.Open')}</button>`
			: ''

		return `<span class="ai-entity-token" data-entity-type="${rawType}" data-entity-id="${id}" data-entity-name="${name}"${routeAttribute} style="position: relative; display: inline-block;">
			<button type="button" class="ai-entity-badge" aria-haspopup="true" aria-expanded="false" style="display: inline-flex; align-items: center; gap: 0.35em; border: 1px solid #b8c7d9; border-radius: 999px; background: #eef5ff; color: #155aa7; padding: 0.05em 0.55em; font: inherit; cursor: pointer; line-height: 1.45;">
				<span style="font-weight: 600;">${name}</span>
				<span style="font-size: 0.75em; opacity: 0.7;">${displayType}</span>
				<span aria-hidden="true" style="font-size: 0.75em;">&#9662;</span>
			</button>
			<span class="ai-entity-menu" role="menu" style="display: none; position: absolute; z-index: 20; left: 0; top: calc(100% + 2px); min-width: 130px; padding: 4px 0; background: white; border: 1px solid #d5d5d5; border-radius: 4px; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);">
				${openAction}
				<button type="button" class="ai-entity-action" data-entity-action="copy-id" role="menuitem" style="display: block; width: 100%; border: 0; background: transparent; padding: 6px 12px; text-align: left; cursor: pointer; font: inherit; color: #333;">${this.i18n.instant('Actions.Copy')}</button>
			</span>
		</span>`
	}

	protected getEntityRoute(_entity: AiEntityToken): string | undefined {
		return undefined
	}

	protected getEntityType(type: string): string {
		return type
	}

	private toggleEntityMenu(token: HTMLElement) {
		const menu = token.querySelector('.ai-entity-menu') as HTMLElement
		const badge = token.querySelector('.ai-entity-badge') as HTMLElement
		if (!menu) return;
		const isOpen = menu.style.display !== 'none'
		;(token.getRootNode() as Document | ShadowRoot).querySelectorAll<HTMLElement>('.ai-entity-token').forEach(t => { if (t !== token) this.closeEntityMenu(t) })
		menu.style.display = isOpen ? 'none' : 'block'
		badge?.setAttribute('aria-expanded', isOpen ? 'false' : 'true')
	}

	public closeAllEntityMenus(root: Document | ShadowRoot | HTMLElement) {
		root.querySelectorAll<HTMLElement>('.ai-entity-token').forEach(t => this.closeEntityMenu(t))
	}

	private closeEntityMenu(token: HTMLElement) {
		const menu = token.querySelector('.ai-entity-menu') as HTMLElement
		const badge = token.querySelector('.ai-entity-badge') as HTMLElement
		if (menu) menu.style.display = 'none'
		badge?.setAttribute('aria-expanded', 'false')
	}

	private closest(element: HTMLElement, className: string): HTMLElement | null {
		let current: HTMLElement | null = element;
		while (current?.classList && current !== document.body) {
			if (current.classList.contains(className)) return current;
			current = current.parentElement;
		}
		return null
	}

	private eventElement(target: EventTarget | null): HTMLElement | null {
		if (target instanceof HTMLElement) return target;
		if (target instanceof Node) return target.parentElement;
		return null
	}

	protected escapeHtml(value: string) {
		return String(value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
	}
}
